import CustomButton from "@/components/common/Button";
import CodeEditor from "@/components/common/CodeEditor";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { filterByLabel } from "@/lib/filterByLabel";
import { DropdownService, PrintFormatService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, Checkbox, Col, Form, Input, Row, Select } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const AddPrintFormatPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use React Query for getting print format by ID
  const { data: printFormat, isLoading } = useQuery({
    queryKey: ["printFormat", id],
    queryFn: () => PrintFormatService.getPrintFormatById(id ?? ""),
    enabled: !!id,
  });

  // Use React Query for schema options
  const { data: schemaOptions, isLoading: schemaLoading } = useQuery({
    queryKey: ["schema"],
    queryFn: () => DropdownService.getDropdownData("schema"),
  });

  useEffect(() => {
    if (isEditMode && printFormat) {
      form.setFieldsValue({
        name: printFormat.name,
        schema_name: printFormat.schema_name,
        is_default: printFormat.is_default,
        template_html: printFormat.template_html ?? "",
        template_css: printFormat.template_css ?? "",
      });
    }
  }, [isEditMode, printFormat, form]);

  // Handle form submission
  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);
    const { template_html, template_css, ...otherValues } = values;
    const payload = {
      ...otherValues,
      template_html: template_html,
      template_css: template_css,
    };

    if (isEditMode && printFormat) {
      // Check for changes before updating
      if (!checkFormChanges(payload, printFormat, notify)) {
        setIsSubmitting(false);
        return;
      }

      const response = await PrintFormatService.updatePrintFormat(
        printFormat.id,
        payload
      );
      if (response) {
        navigate(navigationRoutes.PRINT_FORMAT);
      }
    } else {
      const response = await PrintFormatService.createPrintFormat(payload);
      if (response) {
        navigate(navigationRoutes.PRINT_FORMAT);
      }
    }
    setIsSubmitting(false);
  };

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (isEditMode && !printFormat) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit Print Format" : "Add Print Format"}
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={
                isEditMode
                  ? "Save Print Format Changes"
                  : "Save New Print Format"
              }
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Print Format Name"
              rules={[
                { required: true, message: "Please enter print format name" },
              ]}
            >
              <Input placeholder="Enter print format name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="schema_name"
              label="Schema"
              rules={[{ required: true, message: "Please select a schema" }]}
            >
              <Select
                placeholder="Select schema"
                showSearch
                filterOption={filterByLabel}
                disabled={isEditMode}
                options={schemaOptions}
                loading={schemaLoading}
              />
            </Form.Item>
          </Col>
        </Row>
        <Col span={12}>
          <Form.Item
            name="is_default"
            valuePropName="checked"
            style={{ marginTop: 20 }}
          >
            <Checkbox>Default Print Format</Checkbox>
          </Form.Item>
        </Col>

        <Form.Item
          label="HTML Template"
          name="template_html"
          required
          validateTrigger="onBlur"
          rules={[{ required: true, message: "Please enter HTML template" }]}
        >
          <CodeEditor
            value={form.getFieldValue("template_html")}
            handleChange={(value: string) =>
              form.setFieldsValue({ template_html: value })
            }
            language="html"
          />
        </Form.Item>

        <Form.Item
          label="CSS Template"
          name="template_css"
          required
          validateTrigger="onBlur"
          rules={[{ required: true, message: "Please enter CSS template" }]}
        >
          <CodeEditor
            value={form.getFieldValue("template_css")}
            handleChange={(value: string) =>
              form.setFieldsValue({ template_css: value })
            }
            language="css"
          />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default AddPrintFormatPage;
